import React, { useCallback } from 'react';
import { PromiseState } from 'react-refetch';
import delve from 'dlv';
import stringToSlug from 'helpers/stringToSlug';

import mapKPIData from '../mappings/mapKPIData';
import connect from 'helpers/apiConnect';
import useExportURL from 'hooks/useExportURL';
import useCountryRadioSpinsData from './utils/useCountryRadioSpinsData';
import useUSMarketRadioSpinsData from './utils/useUSMarketRadioSpinsData';
import getRadioKPIUrl from 'url-constructors/getRadioKPIUrl';
import getRadioSpinsURL from 'url-constructors/getRadioSpinsURL';
import useDateRangeKPI from 'hooks/useDateRangeKPI';
import useMedia from 'hooks/useMedia';
import formatDate from 'helpers/formatDate';

import KeyValueGroup from 'components/base/KeyValueGroup/KeyValueGroup';
import { ReactComponent as RadioIcon } from 'icons/topics/radio.svg';
import {
  Container,
  Header,
  KPIItem as KPIListItem,
  KPIListWrapper,
  KPIListItemContent,
  KPIListItemContentItem,
  KPIListItemContentItemColumn,
  KPIListItemColumn,
  KPIListItemRow,
  KPIListTotal as KPIList,
  TWTDText,
  ListItemWrapper,
  DateRange,
} from '../shared';
import KPIWrapper from '../KPIWrapper/KPIWrapper';
import KPILoader from 'components/utility/ContentLoader/KPILoader/KPILoader';
import ErrorBoundary from 'components/utility/ErrorBoundary/ErrorBoundary';
import RadioSpinsTopTerritories from './RadioSpinsTopTerritories';
import TopUSMarket from './TopUSMarket';
import TopCountry from './TopCountry';
import {
  TopicFilterNames,
  ITopicFilterState,
} from 'components/TopicFilters/shared';
import { CustomRegionFilterType, EntityTypes } from 'context/entityContext';
import { tooltipContent } from 'helpers/getTooltipContent';
import TippyToolTip from 'components/base/TippyToolTip/TippyToolTip';
import { IToolbarItemType } from 'components/utility/GenericComponent/GenericComponent';

interface IDataSourcesProps {
  compareMode: string;
  countryIds: string[];
  entityIds: string[];
  entityType: EntityTypes;
  filters: ITopicFilterState;
  customRegionFilter: any;
}

const dataSources = (
  props: IDataSourcesProps
): {
  radioKPIData: string;
  radioSpinsTWTDCountriesData: string;
  radioSpinsLWCountriesData: string;
  radioSpinsUSMarketsTWTDData?: string;
  radioSpinsUSMarketsLWData?: string;
} => {
  const {
    compareMode,
    countryIds,
    entityIds,
    entityType,
    filters,
    customRegionFilter,
  } = props;

  const options = {
    entityIds,
    entityType,
    countryIds: compareMode === 'country' ? countryIds : filters.country,
    region: filters?.region,
    customRegion: customRegionFilter,
    compareMode,
  };

  const fetches = {
    radioKPIData: getRadioKPIUrl(options),
    radioSpinsTWTDCountriesData: getRadioSpinsURL({
      entityIds,
      entityType,
      spinsType: 'countries',
      sortBy: 'countThisWeekToDate',
      limit: 5,
      region: filters?.region,
      countryIds: filters?.country,
      customRegion: customRegionFilter,
    }),
    radioSpinsLWCountriesData: getRadioSpinsURL({
      entityIds,
      entityType,
      spinsType: 'countries',
      sortBy: 'countLastWeek',
      limit: 5,
      region: filters?.region,
      countryIds: filters?.country,
      customRegion: customRegionFilter,
    }),
    radioSpinsUSMarketsTWTDData: '',
    radioSpinsUSMarketsLWData: '',
  };

  // Only request markets data when the US country filter is applied
  if (filters.country === 'US') {
    fetches.radioSpinsUSMarketsTWTDData = getRadioSpinsURL({
      countryIds: [filters.country],
      entityIds,
      entityType,
      sortBy: 'countThisWeekToDate',
      spinsType: 'markets',
      limit: 5,
    });

    fetches.radioSpinsUSMarketsLWData = getRadioSpinsURL({
      countryIds: [filters.country],
      entityIds,
      entityType,
      sortBy: 'countLastWeek',
      spinsType: 'markets',
      limit: 5,
    });
  }

  // null the US market requests when the country filter isn't right
  if (filters.country !== 'US') {
    delete fetches.radioSpinsUSMarketsTWTDData;
    delete fetches.radioSpinsUSMarketsLWData;
  }

  return fetches;
};

interface IRadioKPIsProps extends IDataSourcesProps {
  allowedFilters: TopicFilterNames[];
  radioKPIData: PromiseState<any>;
  radioSpinsTWTDCountriesData: PromiseState<any>;
  radioSpinsLWCountriesData: PromiseState<any>;
  radioSpinsUSMarketsTWTDData?: PromiseState<any>;
  radioSpinsUSMarketsLWData?: PromiseState<any>;
  entityIds: string[];
  filters: ITopicFilterState;
  compareMode: string;
  dataColors: string[];
  countryIds: string[];
  customRegionFilter: CustomRegionFilterType;
}

const RadioKPIs: React.FC<IRadioKPIsProps> = (props) => {
  const {
    radioKPIData = PromiseState.create(),
    radioSpinsTWTDCountriesData = PromiseState.create(),
    radioSpinsLWCountriesData = PromiseState.create(),
    radioSpinsUSMarketsTWTDData = PromiseState.create(),
    radioSpinsUSMarketsLWData = PromiseState.create(),
    entityIds,
    filters,
    compareMode,
    dataColors,
    countryIds,
  } = props;

  const isMobileView = useMedia('(max-width: 40em)');

  const [thisWeekToDate, lastWeek] = useDateRangeKPI('radio');

  const reason =
    radioKPIData.reason ||
    radioSpinsTWTDCountriesData.reason ||
    radioSpinsLWCountriesData.reason ||
    delve(radioSpinsUSMarketsTWTDData, 'reason') ||
    delve(radioSpinsUSMarketsLWData, 'reason') ||
    null;

  const compareArray = compareMode === 'country' ? countryIds : entityIds;
  const exportUrl = useExportURL(radioKPIData);
  const hasTopUSMarkets = filters.country === 'US';

  const radioKPIs = React.useMemo(() => {
    return mapKPIData({
      data: radioKPIData.value,
      compareMode,
      compareArray,
    });
  }, [radioKPIData.value, compareMode, compareArray]);

  const topCountriesTWTD = useCountryRadioSpinsData(
    radioSpinsTWTDCountriesData
  );
  const topCountriesLW = useCountryRadioSpinsData(radioSpinsLWCountriesData);
  const topUSMarketsTWTD = useUSMarketRadioSpinsData(
    radioSpinsUSMarketsTWTDData
  );
  const topUSMarketsLW = useUSMarketRadioSpinsData(radioSpinsUSMarketsLWData);

  /**
   * Returns a key for TopCountry list items
   */
  const getCountryKey = useCallback(
    (entity, item) => `top-country-${entity.entity.id}-${item.id}`,
    []
  );

  /**
   * Returns a key for TopUSMarket list items
   */
  const getUSMarketKey = useCallback(
    (entity, item) => `top-us-market-${entity.entity.id}-${item.marketId}`,
    []
  );

  return (
    <Container data-pw={stringToSlug('Radio KPIs')}>
      <ErrorBoundary error={reason}>
        <KPIWrapper
          allowedFilters={['country', 'region', 'customRegion']}
          glossarySlug="kpis/radio-kpis"
          title="Radio KPIs"
          icon={RadioIcon}
          extraToolbarItems={[
            {
              label: 'Export Radio KPI data',
              href: exportUrl,
              type: IToolbarItemType.Export,
              order: 1,
            },
          ]}
          titleAlignLeft="-1.5em"
          alignSVG="1.75em"
        >
          <KPIListWrapper>
            <KPIList>
              <KPIListItem>
                <KPIListItemContent>
                  <KPIListItemContentItem itemsCount={3}>
                    <KPIListItemRow>
                      <ListItemWrapper>
                        <Header>
                          <TippyToolTip
                            enabled
                            tooltipContent={tooltipContent}
                            offset={[10, 25]}
                            maxWidth="170px"
                          >
                            <TWTDText>{`This Week ${
                              !isMobileView ? 'to Date' : ''
                            }`}</TWTDText>
                          </TippyToolTip>
                          {thisWeekToDate && (
                            <DateRange>
                              {`${formatDate(
                                thisWeekToDate?.startDate,
                                'day',
                                true
                              )} 
                              - ${formatDate(
                                thisWeekToDate?.endDate,
                                'day',
                                true
                              )}`}
                            </DateRange>
                          )}
                        </Header>
                        {!radioKPIs && <KPILoader showPrevious showChange />}
                        {radioKPIs && (
                          <KeyValueGroup
                            items={[
                              {
                                label: 'Radio spins',
                                metric: delve(radioKPIs, 'countThisWeekToDate'),
                              },
                              {
                                label: 'Previous',
                                metric: delve(radioKPIs, 'countLastWeekToDate'),
                              },
                              {
                                label: 'Change',
                                metric: delve(radioKPIs, 'wowInProgress'),
                              },
                            ]}
                            dataColors={dataColors}
                            compareMode={compareMode}
                            isKPIValue
                          />
                        )}
                      </ListItemWrapper>
                    </KPIListItemRow>
                    <KPIListItemColumn>
                      <KPIListItemContentItemColumn>
                        <RadioSpinsTopTerritories
                          label="Top Countries"
                          pending={radioSpinsTWTDCountriesData.pending}
                          topTerritories={topCountriesTWTD}
                          TerritoryItem={TopCountry}
                          getKey={getCountryKey}
                        />
                      </KPIListItemContentItemColumn>
                      <KPIListItemContentItemColumn>
                        {hasTopUSMarkets && (
                          <RadioSpinsTopTerritories
                            label="Top US Markets"
                            pending={radioSpinsUSMarketsTWTDData.pending}
                            type="city"
                            topTerritories={topUSMarketsTWTD}
                            TerritoryItem={TopUSMarket}
                            getKey={getUSMarketKey}
                          />
                        )}
                      </KPIListItemContentItemColumn>
                    </KPIListItemColumn>
                  </KPIListItemContentItem>

                  <KPIListItemContentItem itemsCount={3}>
                    <KPIListItemRow>
                      <ListItemWrapper>
                        <Header>
                          <TWTDText>
                            {`Last ${!isMobileView ? 'Full ' : ''}Week`}
                          </TWTDText>
                          {lastWeek && (
                            <DateRange>
                              {`${formatDate(lastWeek?.startDate, 'day', true)} 
                            - ${formatDate(lastWeek?.endDate, 'day', true)}`}
                            </DateRange>
                          )}
                        </Header>
                        {!radioKPIs && <KPILoader showPrevious showChange />}
                        {radioKPIs && (
                          <KeyValueGroup
                            items={[
                              {
                                label: 'Radio spins',
                                metric: delve(radioKPIs, 'countLastWeek'),
                              },
                              {
                                label: 'Previous',
                                metric: delve(radioKPIs, 'countLastLastWeek'),
                              },
                              {
                                label: 'Change',
                                metric: delve(radioKPIs, 'wowLastCompleted'),
                              },
                            ]}
                            dataColors={dataColors}
                            compareMode={compareMode}
                            isKPIValue
                          />
                        )}
                      </ListItemWrapper>
                    </KPIListItemRow>
                    <KPIListItemColumn>
                      <KPIListItemContentItemColumn>
                        <RadioSpinsTopTerritories
                          label="Top Countries"
                          pending={radioSpinsLWCountriesData.pending}
                          topTerritories={topCountriesLW}
                          TerritoryItem={TopCountry}
                          getKey={getCountryKey}
                        />
                      </KPIListItemContentItemColumn>
                      <KPIListItemContentItemColumn>
                        {hasTopUSMarkets && (
                          <RadioSpinsTopTerritories
                            label="Top US Markets"
                            pending={radioSpinsUSMarketsLWData.pending}
                            type="city"
                            topTerritories={topUSMarketsLW}
                            TerritoryItem={TopUSMarket}
                            getKey={getUSMarketKey}
                          />
                        )}
                      </KPIListItemContentItemColumn>
                    </KPIListItemColumn>
                  </KPIListItemContentItem>

                  <KPIListItemContentItem itemsCount={3}>
                    <KPIListItemRow>
                      <ListItemWrapper>
                        <Header>
                          <TippyToolTip
                            enabled
                            tooltipContent={tooltipContent}
                            offset={[10, 25]}
                            maxWidth="170px"
                          >
                            <TWTDText>Release to Date</TWTDText>
                          </TippyToolTip>
                        </Header>
                        {!radioKPIs && <KPILoader />}
                        {radioKPIs && (
                          <KeyValueGroup
                            items={[
                              {
                                label: 'Radio spins',
                                metric: delve(radioKPIs, 'countRtd'),
                              },
                            ]}
                            dataColors={dataColors}
                            compareMode={compareMode}
                            isKPIValue
                          />
                        )}
                      </ListItemWrapper>
                    </KPIListItemRow>
                  </KPIListItemContentItem>
                </KPIListItemContent>
              </KPIListItem>
            </KPIList>
          </KPIListWrapper>
        </KPIWrapper>
      </ErrorBoundary>
    </Container>
  );
};

export default connect(dataSources as any)(RadioKPIs as any) as any;
